import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { FolderKanban, Mail, Users, TrendingUp } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import axios from '../../config/axios';

const Dashboard = () => {
  const [stats, setStats] = useState({
    projects: 0,
    contacts: 0,
    unreadContacts: 0,
    users: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [projectsRes, contactsRes, usersRes] = await Promise.all([
        axios.get('/admin/projects'),
        axios.get('/admin/contacts/stats'),
        axios.get('/admin/users')
      ]);

      setStats({
        projects: projectsRes.data.data.total || 0,
        contacts: contactsRes.data.data.total || 0,
        unreadContacts: contactsRes.data.data.unread || 0,
        users: usersRes.data.data.total || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Projects',
      value: stats.projects,
      icon: FolderKanban,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600'
    },
    {
      title: 'Total Messages',
      value: stats.contacts,
      icon: Mail,
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600'
    },
    {
      title: 'Unread Messages',
      value: stats.unreadContacts,
      icon: TrendingUp,
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50',
      iconColor: 'text-orange-600'
    },
    {
      title: 'Total Users',
      value: stats.users,
      icon: Users,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600'
    }
  ];

  return (
    <AdminLayout>
      <Helmet>
        <title>Dashboard - Admin Portal | BURALE</title>
      </Helmet>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-burale-dark mb-2">Dashboard</h1>
        <p className="text-gray-600">Welcome to BURALE Admin Portal</p>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-burale-teal"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <div
                key={index}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden"
              >
                <div className={`bg-gradient-to-r ${card.color} p-6`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white text-sm font-medium opacity-90">{card.title}</p>
                      <h3 className="text-white text-4xl font-bold mt-2">{card.value}</h3>
                    </div>
                    <div className={`${card.bgColor} p-4 rounded-lg`}>
                      <Icon className={card.iconColor} size={32} />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-8 bg-white rounded-xl shadow-md p-6">
        <h2 className="text-2xl font-bold text-burale-dark mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <a
            href="/admin/projects"
            className="p-4 border-2 border-burale-teal rounded-lg hover:bg-burale-teal hover:text-white transition-all duration-300 text-center"
          >
            <FolderKanban className="mx-auto mb-2" size={32} />
            <p className="font-semibold">Manage Projects</p>
          </a>
          <a
            href="/admin/contacts"
            className="p-4 border-2 border-burale-teal rounded-lg hover:bg-burale-teal hover:text-white transition-all duration-300 text-center"
          >
            <Mail className="mx-auto mb-2" size={32} />
            <p className="font-semibold">View Messages</p>
          </a>
          <a
            href="/admin/users"
            className="p-4 border-2 border-burale-teal rounded-lg hover:bg-burale-teal hover:text-white transition-all duration-300 text-center"
          >
            <Users className="mx-auto mb-2" size={32} />
            <p className="font-semibold">Manage Users</p>
          </a>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
